import { defineStore } from "pinia"

// Services
import api from "@/services/apiClient"

// Types
import ReportStatus, { ActiveSubscriptionReportStatusValues } from "@/shared/enums/ReportStatus"
import CoachingStatus, { ActiveSubscriptionCoachingStatusValues } from "@/shared/enums/CoachingStatus"
import type UserGetMeOut from "@/shared/dtos/out/UserGetMeOut"

export const useUserStore = defineStore("user", {
  state: () => ({
    userData: {} as UserGetMeOut
  }),
  getters: {
    getEmail: (state) => {
      return state.userData?.email || null
    },
    getFullName: (state) => {
      return state.userData?.fullName || null
    },
    getPhoneNumber: (state) => {
      return state.userData?.phoneNumber || null
    },
    getGender: (state) => {
      return state.userData?.gender || null
    },
    getBirthday: (state) => {
      return state.userData?.birthday || null
    },
    getQuizPage: (state) => {
      return state.userData?.quizPage || null
    },
    getUserData: (state) => {
      return state.userData
    },
    getUserId: (state) => {
      return state.userData?.id || null
    },
    getUserInitials: (state) => {
      const fullName = state.userData?.fullName?.trim()
      if (!fullName) return ""

      const fullNameArray = fullName.split(" ")
      const arrLength = fullNameArray.length

      const firstInitial = fullNameArray[0][0]
      const lastInitial = arrLength > 1 ? fullNameArray[arrLength - 1][0] : ""

      return `${firstInitial}${lastInitial}`.toUpperCase()
    },
    getReportStatus: (state) => {
      return state.userData?.reportStatus || null
    },
    getCoachingStatus: (state) => {
      return state.userData?.coachingStatus || null
    },
    getZipCode: (state) => {
      return state.userData?.zipCode || null
    },
    /**
     * Get user's available scopingBooking credits
     */
    getScopingCredits: (state) => {
      return state.userData?.scopingCreditsAvailable || 0
    },
    /**
     * Get user's available onDemandBooking credits
     */
    getOnDemandCredits: (state) => {
      return state.userData?.onDemandCreditsAvailable || 0
    },
    /**
     * Get user's available coachingBooking credits
     */
    getCoachingCredits: (state) => {
      return state.userData?.coachingCreditsAvailable || 0
    },
    /**
     * Has completed a quiz that gives them access to coaching pages.
     * - Coaching status is not `CoachingStatus.NONE`
     */
    isCoachingUser: (state) => {
      return !!state.userData?.coachingStatus && state.userData?.coachingStatus !== CoachingStatus.NONE
    },
    /**
     * Has completed a quiz that gives them access to report pages.
     * - Report status is not `ReportStatus.NONE`
     */
    isReportUser: (state) => {
      return !!state.userData?.reportStatus && state.userData?.reportStatus !== ReportStatus.NONE
    },
    /**
     * Has completed a quiz that gives them access to report pages.
     * - Has an active report subscription (i.e Report status is `ReportStatus.MONTHLY` or `ReportStatus.QUARTERLY` or `ReportStatus.ANNUAL`)
     */
    isActiveReportSubscriber: (state) => {
      const userReportStatus = state.userData?.reportStatus
      return !!userReportStatus && ActiveSubscriptionReportStatusValues.includes(userReportStatus)
    },
    /**
     * Has completed a quiz that gives them access to coaching pages, but not report pages.
     * - Coaching status is not `CoachingStatus.NONE`
     * - Report status is `ReportStatus.NONE`
     */
    isCoachingOnlyUser: (state) => {
      return (
        state.userData?.coachingStatus !== CoachingStatus.NONE && state.userData?.reportStatus === ReportStatus.NONE
      )
    },
    /**
     * Has paid for a coaching product.
     * - Coaching status is not `CoachingStatus.NONE` or `CoachingStatus.FINISHED`
     */
    isCoachingHasEngaged: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return (
        !!userCoachingStatus &&
        [
          CoachingStatus.ONCE,
          CoachingStatus.PROFESSIONAL,
          CoachingStatus.EXECUTIVE,
          CoachingStatus.WEEKLY,
          CoachingStatus.BIWEEKLY,
          CoachingStatus.MONTHLY,
          CoachingStatus.CANCELED
        ].includes(userCoachingStatus)
      )
    },
    /**
     * Has never paid for a coaching product.
     * - Coaching status is `CoachingStatus.NONE` or `CoachingStatus.FINISHED`
     */
    isCoachingHasNotEngaged: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return !!userCoachingStatus && [CoachingStatus.NONE, CoachingStatus.FINISHED].includes(userCoachingStatus)
    },
    /**
     * Had a coaching subscription that has been canceled.
     * - Coaching status is `CoachingStatus.CANCELED`
     */
    isCoachingCancelled: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return !!userCoachingStatus && userCoachingStatus === CoachingStatus.CANCELED
    },
    /**
     * Has completed a coaching session.
     * - Coaching status is `CoachingStatus.ONCE`
     */
    isCoachingOnce: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return !!userCoachingStatus && userCoachingStatus === CoachingStatus.ONCE
    },
    /**
     * Has a coaching subscription that is active or had a one-time session.
     * - Coaching status is not `CoachingStatus.NONE` or `CoachingStatus.CANCELED` or `CoachingStatus.FINISHED`
     */
    isCoachingPaiduser: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return (
        !!userCoachingStatus &&
        [
          CoachingStatus.ONCE,
          CoachingStatus.PROFESSIONAL,
          CoachingStatus.EXECUTIVE,
          CoachingStatus.WEEKLY,
          CoachingStatus.BIWEEKLY,
          CoachingStatus.MONTHLY
        ].includes(userCoachingStatus)
      )
    },
    /**
     * Has a coaching subscription that is active.
     */
    isCoachingSubscriber: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return !!userCoachingStatus && ActiveSubscriptionCoachingStatusValues.includes(userCoachingStatus)
    },
    hasCoachingBundle: (state) => {
      return state.userData?.hasCoachingBundlePackage || false
    },
    hasPurchasedExecutiveSubscription: (state) => {
      return state.userData?.hasPurchasedExecutiveSubscription || false
    },
    hasPurchasedExecutiveDiscovery: (state) => {
      return state.userData?.hasPurchasedExecutiveDiscovery || false
    },
    isCoachingExecutiveOrProfessionalSubscriber: (state) => {
      const userCoachingStatus = state.userData?.coachingStatus
      return (
        !!userCoachingStatus && [CoachingStatus.PROFESSIONAL, CoachingStatus.EXECUTIVE].includes(userCoachingStatus)
      )
    },
    /**
     * Is a potential client for Executive Coaching (i.e coaching quiz currentSalary >= 125000)
     */
    isExecutiveCoachingPotential: (state) => {
      return state.userData?.isExecutiveCoachingPotential || false
    },
    isFeedbackExperienceCompleted: (state) => {
      return state.userData?.isFeedbackExperienceCompleted || false
    },
    isGoogleLinked: (state) => {
      return state.userData?.isGoogleLinked || false
    },
    isLoggedIn: (state) => {
      return !!state.userData?.id
    },
    isTierFinished: (state) => {
      return state.userData?.reportStatus === ReportStatus.FINISHED
    },
    isTierFinishedOrHigher: (state) => {
      return state.userData?.reportStatus ?? false
    },
    isTierFree: (state) => {
      return state.userData?.reportStatus === ReportStatus.FREE
    },
    isTierFreeOrHigher: (state) => {
      return state.userData?.reportStatus !== ReportStatus.FINISHED
    },
    isTierAnyPaid: (state) => {
      return state.userData?.reportStatus
        ? [
            ReportStatus.BASIC,
            ReportStatus.COMPREHENSIVE,
            ReportStatus.MONTHLY,
            ReportStatus.QUARTERLY,
            ReportStatus.ANNUAL,
            ReportStatus.CANCELED,
            ReportStatus.PREMIUM
          ].includes(state.userData.reportStatus)
        : false
    },
    isTierBasic: (state) => {
      return state.userData?.reportStatus === ReportStatus.BASIC
    },
    isTierComprehensive: (state) => {
      return state.userData?.reportStatus
        ? [
            ReportStatus.COMPREHENSIVE,
            ReportStatus.MONTHLY,
            ReportStatus.QUARTERLY,
            ReportStatus.ANNUAL,
            ReportStatus.CANCELED
          ].includes(state.userData.reportStatus)
        : false
    },
    isTierComprehensiveOrHigher: (state) => {
      return state.userData?.reportStatus
        ? [
            ReportStatus.COMPREHENSIVE,
            ReportStatus.MONTHLY,
            ReportStatus.QUARTERLY,
            ReportStatus.ANNUAL,
            ReportStatus.CANCELED,
            ReportStatus.PREMIUM
          ].includes(state.userData.reportStatus)
        : false
    },
    isTierPremium: (state) => {
      return state.userData?.reportStatus === ReportStatus.PREMIUM
    }
  },
  actions: {
    async checkSessionValidity() {
      return (await api.auth.getCheckSession()).valid
    },

    async loadUserData() {
      try {
        const data = await api.user.getMe()
        this.userData = data || {}
      } catch (error) {
        console.error("Error loading user's data", error)
      }
    },

    async logout() {
      try {
        await api.auth.postLogoutApp()
        this.userData = {} as UserGetMeOut
      } catch (error) {
        console.error("Error logging out", error)
      }
    }
  }
})
